// src/pages/dashboard/shared/Support.jsx
import { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { error, success } from '../../../components/common/Toast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { 
  AlertCircle, 
  FileText, 
  Clock, 
  CheckCircle, 
  X, 
  Upload, 
  Image, 
  File, 
  AlertTriangle,
  Send,
  ChevronRight,
  ChevronLeft,
  MessageSquare
} from 'lucide-react';
import { formatDate, formatMoney } from '../../../lib/helpers';
import { useAuth } from '../../../contexts/AuthContext';
import useEmblaCarousel from 'embla-carousel-react';

const MAX_FILES = 5;
const MAX_SIZE_MB = 10;

// Predefined complaint subjects
const COMPLAINT_SUBJECTS = [
  { value: 'fake_profile', label: 'Fake or suspicious profile' },
  { value: 'harassment', label: 'Harassment or inappropriate behavior' },
  { value: 'payment_issue', label: 'Payment or contract issue' },
  { value: 'app_bug', label: 'App not working properly' },
  { value: 'bad_experience', label: 'Bad experience with nanny/customer' },
  { value: 'account_issue', label: 'Login or account problem' },
  { value: 'other', label: 'Other issue' },
];

export default function Support() {
  const [activeTab, setActiveTab] = useState('disputes');
  const [disputes, setDisputes] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [submittingDispute, setSubmittingDispute] = useState(false);
  const [submittingComplaint, setSubmittingComplaint] = useState(false);
  const [submittingAppeal, setSubmittingAppeal] = useState({});

  // Carousel for Complaints
  const [complaintEmblaRef, complaintEmblaApi] = useEmblaCarousel({ loop: false });
  const [complaintIndex, setComplaintIndex] = useState(0);

  // Forms
  const [selectedContract, setSelectedContract] = useState('');
  const [disputeDetails, setDisputeDetails] = useState('');
  const [disputeFiles, setDisputeFiles] = useState([]);

  const [selectedSubject, setSelectedSubject] = useState('');
  const [complaintDetails, setComplaintDetails] = useState('');
  const [complaintFiles, setComplaintFiles] = useState([]);

  // Appeal form
  const [appealModal, setAppealModal] = useState({ open: false, dispute: null });
  const [appealMessage, setAppealMessage] = useState('');
  const [appealFiles, setAppealFiles] = useState([]);

  // Carousel
  const [disputeEmblaRef, disputeEmblaApi] = useEmblaCarousel({ loop: false });
  const [disputeIndex, setDisputeIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [disputeRes, complaintRes, contractRes] = await Promise.all([
        api.get('/support/disputes'),
        api.get('/support/complaints'),
        api.get('/contracts/current')
      ]);

      setDisputes(disputeRes.data.data || []);
      setComplaints(complaintRes.data.data || []);
      setContracts(contractRes.data.data || []);
    } catch (err) {
      error('Failed to load support data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disputeEmblaApi) {
      const onSelect = () => setDisputeIndex(disputeEmblaApi.selectedScrollSnap());
      disputeEmblaApi.on('select', onSelect);
      return () => disputeEmblaApi.off('select', onSelect);
    }
  }, [disputeEmblaApi]);

  const handleFileChange = (e, setter, currentFiles) => {
    const newFiles = Array.from(e.target.files || []);
    const allFiles = [...currentFiles, ...newFiles].slice(0, MAX_FILES);

    const validFiles = allFiles.filter(file => {
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        error(`${file.name} is too large (max ${MAX_SIZE_MB}MB)`);
        return false;
      }
      return true;
    });

    setter(validFiles);
  };

  const removeFile = (index, files, setter) => {
    setter(files.filter((_, i) => i !== index));
  };

  const handleDisputeSubmit = async () => {
    if (!selectedContract || !disputeDetails.trim()) {
      error('Contract and details are required');
      return;
    }

    const formData = new FormData();
    formData.append('contract_id', selectedContract);
    formData.append('dispute_details', disputeDetails);
    disputeFiles.forEach(file => formData.append('file[]', file));

    setSubmittingDispute(true);
    try {
      await api.post('/support/disputes', formData, {
        headers:{ 
          "Content-Type": "multipart/form-data"
        }
      });
      success('Dispute raised successfully');
      setDisputeDetails('');
      setDisputeFiles([]);
      setSelectedContract('');
      fetchData();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to raise dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const handleComplaintSubmit = async () => {
    if (!selectedSubject || !complaintDetails.trim()) {
      error('Please select a subject and describe the issue');
      return;
    }

    const formData = new FormData();
    formData.append('subject', selectedSubject);
    formData.append('complaint_details', complaintDetails);
    complaintFiles.forEach(file => formData.append('file[]', file));

    setSubmittingComplaint(true);
    try {
      await api.post('/support/complaints', formData, {
        headers:{ 
          "Content-Type": "multipart/form-data"
        }
      });
      success('Complaint submitted successfully');
      setSelectedSubject('');
      setComplaintDetails('');
      setComplaintFiles([]);
      fetchData();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to submit complaint');
    } finally {
      setSubmittingComplaint(false);
    }
  };

  const handleAppeal = async () => {
    if (!appealMessage.trim()) {
      error('Please explain your side of the story');
      return;
    }

    const formData = new FormData();
    formData.append('dispute_details', appealMessage);
    appealFiles.forEach(file => formData.append('file[]', file));

    setSubmittingAppeal(prev => ({ ...prev, [appealModal.dispute.id]: true }));
    try {
      await api.post(`/support/dispute/${appealModal.dispute.id}/appeal`, formData, {
        headers:{ 
          "Content-Type": "multipart/form-data"
        }
      });
      success('Appeal submitted successfully');
      setAppealModal({ open: false, dispute: null });
      setAppealMessage('');
      setAppealFiles([]);
      fetchData();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to submit appeal');
    } finally {
      setSubmittingAppeal(prev => ({ ...prev, [appealModal.dispute.id]: false }));
    }
  };

  const getOtherParty = (dispute) => {
    const profileId = user?.profile?.id || user?.id;
    const contract = dispute.contract;

    if (!contract) return { name: 'Unknown', role: '' };

    if (contract.nanny_profile_id === profileId) {
      return {
        name: `${contract.customer.first_name} ${contract.customer.last_name}`,
        role: 'Customer',
        photo: contract.customer.profile_picture
      };
    } else {
      return {
        name: `${contract.nanny.first_name} ${contract.nanny.last_name}`,
        role: 'Nanny',
        photo: contract.nanny.profile_picture
      };
    }
  };

  const FilePreview = ({ file, index, files, setFiles }) => {
    const isImage = file?.type.startsWith('image/');
    const isPDF = file?.type === 'application/pdf';

    return (
      <div className="relative group">
        {isImage ? (
          <img src={URL.createObjectURL(file)} alt="" className="w-32 h-32 object-cover rounded-xl shadow-lg" />
        ) : isPDF ? (
          <div className="w-32 h-32 bg-red-50 rounded-xl flex items-center justify-center">
            <FileText size={60} className="text-red-600" />
          </div>
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded-xl flex items-center justify-center">
            <File size={60} className="text-gray-500" />
          </div>
        )}
        <button
          onClick={() => removeFile(index, files, setFiles)}
          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition"
        >
          <X size={18} />
        </button>
        <p className="text-sm text-center mt-2 truncate w-32">{file?.name}</p>
        <p className="text-xs text-gray-500 text-center">{(file?.size / 1024 / 1024).toFixed(1)} MB</p>
      </div>
    );
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="max-w-6xl mx-auto space-y-12 py-10">
      <div className="text-center">
        <h1 className="text-5xl font-black">Support Center</h1>
        <p className="text-2xl text-gray-600 mt-4">We're here to help you</p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center gap-12 border-b-2">
        <button
          onClick={() => setActiveTab('disputes')}
          className={`py-4 px-10 text-2xl font-bold border-b-4 transition-all ${
            activeTab === 'disputes'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500'
          }`}
        >
          Contract Disputes ({disputes.length})
        </button>
        <button
          onClick={() => setActiveTab('complaints')}
          className={`py-4 px-10 text-2xl font-bold border-b-4 transition-all ${
            activeTab === 'complaints'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500'
          }`}
        >
          General Complaints ({complaints.length})
        </button>
      </div>

      {/* === DISPUTES TAB === */}
      {activeTab === 'disputes' && (
        <>
          {/* Raise Dispute Form */}
          <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-3xl p-10 border-2 border-red-200">
            <h2 className="text-3xl font-bold text-red-800 mb-8">Raise a Contract Dispute</h2>

            {contracts.length === 0 ? (
              <p className="text-xl text-gray-700">No active contracts to dispute</p>
            ) : (
              <div className="space-y-10">
                <div>
                  <label className="block text-xl font-bold mb-4">Select Contract</label>
                  <select
                    value={selectedContract}
                    onChange={(e) => setSelectedContract(e.target.value)}
                    className="w-full px-6 py-5 border-2 rounded-2xl text-lg focus:ring-4 focus:ring-red-200"
                  >
                    <option value="">Choose contract...</option>
                    {contracts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.nanny?.full_name || c.customer?.full_name} – ₦{c.monthly_salary}/mo
                      </option>
                    ))}
                  </select>
                </div>

                <textarea
                  rows={6}
                  value={disputeDetails}
                  onChange={(e) => setDisputeDetails(e.target.value)}
                  placeholder="Describe what happened in detail..."
                  className="w-full px-6 py-5 border-2 rounded-2xl text-lg focus:ring-4 focus:ring-red-200"
                />

                {/* File Upload */}
                <div>
                  <label className="block text-xl font-bold mb-6">Attach Evidence (Optional)</label>
                  <div className="border-4 border-dashed border-gray-300 rounded-3xl p-12 text-center bg-white hover:border-primary transition">
                    <Upload className="mx-auto mb-6 text-gray-500" size={64} />
                    <p className="text-xl text-gray-700">Drop files here or click to browse</p>
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, setDisputeFiles, disputeFiles)}
                      className="hidden"
                      id="dispute-upload"
                    />
                    <label
                      htmlFor="dispute-upload"
                      className="inline-block mt-6 px-10 py-4 bg-primary text-white rounded-xl font-bold cursor-pointer hover:bg-primary/90"
                    >
                      Choose Files
                    </label>
                  </div>

                  {disputeFiles.length > 0 && (
                    <div className="mt-8">
                      <h3 className="text-xl font-bold mb-6">{disputeFiles.length} file{disputeFiles.length > 1 ? 's' : ''} selected</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {disputeFiles.map((file, i) => (
                          <FilePreview key={i} file={file} index={i} files={disputeFiles} setFiles={setDisputeFiles} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={handleDisputeSubmit}
                  disabled={submittingDispute}
                  className="w-full py-6 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white text-2xl font-bold rounded-3xl flex items-center justify-center gap-4 transition"
                >
                  {submittingDispute ? (
                    <>
                      <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={32} />
                      Submit Dispute
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Disputes List */}
          {disputes.length > 0 ? (
            <div className="relative">
              <div className="overflow-hidden rounded-3xl" ref={disputeEmblaRef}>
                <div className="flex">
                  {disputes.map((dispute) => {
                    const party = getOtherParty(dispute);
                    const isRaisedByMe = dispute.raised_by_me;
                    const canAppeal = dispute.can_appeal;

                    return (
                      <div key={dispute.id} className="flex-none w-full px-6">
                        <div className={`rounded-3xl shadow-2xl p-12 border-l-8 ${
                          isRaisedByMe ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-500' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-500'
                        }`}>
                          <div className="flex items-center justify-between mb-8">
                            <div className="flex items-center gap-6">
                              <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center text-3xl font-bold text-gray-700 shadow">
                                {party.photo && !party.photo.includes('default-avatar') ? 
                                  <img
                                    src={process.env.REACT_APP_IMAGE_URL+'storage/'+party.photo}
                                    alt={party.name}
                                    className="w-full h-full object-cover rounded-3xl"
                                  /> : 
                                  party?.name?.[0]?.toUpperCase() || 'U'
                                }
                              </div>
                              <div>
                                <p className="text-xl text-gray-600">
                                  {isRaisedByMe ? 'You raised a dispute against' : 'Dispute raised against you by'}
                                </p>
                                <p className="text-3xl font-bold">
                                  {party.name} <span className="text-xl text-gray-500">({party.role})</span>
                                </p>
                                <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-xs sm:text-sm md:text-base transition-all mt-2 ${
                                  dispute.status === 'open' 
                                    ? 'bg-yellow-100 text-yellow-800' 
                                    : dispute.status === 'in_review' 
                                      ? 'bg-purple-100 text-purple-800' 
                                      : 'bg-green-100 text-green-800'
                                }`}>
                                  {dispute.status === 'open' && <Clock size={14} />}
                                  {dispute.status === 'in_review' && <AlertTriangle size={14} />}
                                  {dispute.status === 'closed' && <CheckCircle size={14} />}
                                  <span className="uppercase tracking-wider">
                                    {dispute.status.replace('_', ' ')}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="bg-white rounded-2xl p-8 mb-8">
                            <p className="text-xl leading-relaxed">{dispute.dispute_details}</p>
                          </div>

                          <div className="flex justify-between items-center">
                            <div className="text-lg">
                              <span className="text-gray-500">Contract Salary:</span>{' '}
                              <span className="font-bold">{formatMoney(dispute.contract.monthly_salary)}/mo</span>
                            </div>
                            <div className="text-lg text-gray-500">
                              {formatDate(dispute.created_at)}
                            </div>
                          </div>

                          {canAppeal && (
                            <button
                              onClick={() => setAppealModal({ open: true, dispute })}
                              className="mt-8 w-full py-5 bg-purple-600 hover:bg-purple-700 text-white text-xl font-bold rounded-2xl flex items-center justify-center gap-3 transition"
                            >
                              <MessageSquare size={28} />
                              Submit Appeal
                            </button>
                          )}

                          {dispute.appeal && (
                            <div className="mt-8 p-6 bg-purple-50 rounded-2xl border-2 border-purple-300">
                              <p className="font-bold text-purple-900 text-xl mb-3">Appeal</p>
                              <p className="text-lg">{dispute.appeal.dispute_details}</p>
                              <p className="text-sm text-purple-700 mt-4">
                                Submitted on {formatDate(dispute.appeal.created_at)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {disputes.length > 1 && (
                <>
                  <button
                    onClick={() => disputeEmblaApi?.scrollPrev()}
                    disabled={disputeIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-4 rounded-full shadow-2xl disabled:opacity-30"
                  >
                    <ChevronLeft size={48} />
                  </button>
                  <button
                    onClick={() => disputeEmblaApi?.scrollNext()}
                    disabled={disputeIndex === disputes.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-4 rounded-full shadow-2xl disabled:opacity-30"
                  >
                    <ChevronRight size={48} />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-20 bg-gray-50 rounded-3xl">
              <AlertCircle size={80} className="mx-auto text-gray-300 mb-6" />
              <p className="text-2xl text-gray-600">No disputes found</p>
            </div>
          )}
        </>
      )}

      {/* APPEAL MODAL */}
      {appealModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setAppealModal({ open: false, dispute: null })}>
          <div className="bg-white rounded-3xl p-10 max-w-3xl w-full max-h-screen overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-3xl font-bold text-center mb-8 text-purple-800">
              Respond to Dispute
            </h2>

            <div className="bg-gray-50 rounded-2xl p-6 mb-8">
              <p className="text-xl"><strong>Original Dispute:</strong></p>
              <p className="mt-3 text-lg italic">"{appealModal.dispute.dispute_details}"</p>
            </div>

            <textarea
              rows={8}
              value={appealMessage}
              onChange={e => setAppealMessage(e.target.value)}
              placeholder="Explain your side of the story with facts..."
              className="w-full px-6 py-5 border-2 rounded-2xl text-lg focus:ring-4 focus:ring-purple-200"
            />

            <div className="mt-8">
              <label className="block text-xl font-bold mb-6">Attach Evidence (Optional)</label>
              <div className="border-4 border-dashed border-gray-300 rounded-3xl p-12 text-center bg-white hover:border-purple-400 transition">
                <Upload className="mx-auto mb-6 text-gray-500" size={64} />
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileChange(e, setAppealFiles, appealFiles)}
                  className="hidden"
                  id="appeal-upload"
                />
                <label
                  htmlFor="appeal-upload"
                  className="inline-block mt-6 px-10 py-4 bg-purple-600 text-white rounded-xl font-bold cursor-pointer hover:bg-purple-700"
                >
                  Choose Files
                </label>
              </div>

              {appealFiles.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-6">{appealFiles.length} file{appealFiles.length > 1 ? 's' : ''} attached</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {appealFiles.map((file, i) => (
                      <FilePreview key={i} file={file} index={i} files={appealFiles} setFiles={setAppealFiles} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-4 mt-10">
              <button
                onClick={() => setAppealModal({ open: false, dispute: null })}
                className="px-8 py-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAppeal}
                disabled={submittingAppeal[appealModal.dispute.id]}
                className="px-12 py-4 bg-purple-600 hover:bg-purple-700 disabled:opacity-70 text-white font-bold rounded-xl flex items-center gap-3 transition"
              >
                {submittingAppeal[appealModal.dispute.id] ? (
                  <>
                    <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Appeal'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === COMPLAINTS TAB === */}
      {activeTab === 'complaints' && (
        <>
          {/* Complaint Form - Now with predefined subjects */}
          <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl p-10 border-2 border-yellow-300">
            <h2 className="text-3xl font-bold text-yellow-900 mb-8">File a General Complaint</h2>

            <div className="space-y-10">
              {/* Predefined Subject Dropdown */}
              <div>
                <label className="block text-xl font-bold mb-4">Select Issue Type</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="w-full px-6 py-5 border-2 rounded-2xl text-lg focus:ring-4 focus:ring-yellow-300 bg-white"
                >
                  <option value="">Choose a subject...</option>
                  {COMPLAINT_SUBJECTS.map(sub => (
                    <option key={sub.value} value={sub.value}>
                      {sub.label}
                    </option>
                  ))}
                </select>
              </div>

              <textarea
                rows={8}
                placeholder="Describe the issue in detail..."
                value={complaintDetails}
                onChange={(e) => setComplaintDetails(e.target.value)}
                className="w-full px-6 py-5 border-2 rounded-2xl text-lg focus:ring-4 focus:ring-yellow-300"
              />

              {/* File Upload */}
              <div>
                <label className="block text-xl font-bold mb-6">Attach Screenshots (Optional)</label>
                <div className="border-4 border-dashed border-gray-300 rounded-3xl p-12 text-center bg-white hover:border-yellow-400 transition">
                  <Upload className="mx-auto mb-6 text-gray-500" size={64} />
                  <p className="text-xl text-gray-700">Drop files here or click</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileChange(e, setComplaintFiles, complaintFiles)}
                    className="hidden"
                    id="complaint-upload"
                  />
                  <label
                    htmlFor="complaint-upload"
                    className="inline-block mt-6 px-10 py-4 bg-yellow-600 text-white rounded-xl font-bold cursor-pointer hover:bg-yellow-700"
                  >
                    Choose Files
                  </label>
                </div>

                {complaintFiles.length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-xl font-bold mb-6">{complaintFiles.length} file{complaintFiles.length > 1 ? 's' : ''} attached</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      {complaintFiles.map((file, i) => (
                        <FilePreview key={i} file={file} index={i} files={complaintFiles} setFiles={setComplaintFiles} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleComplaintSubmit}
                disabled={submittingComplaint}
                className="w-full py-6 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-70 text-white text-2xl font-black rounded-3xl shadow-xl transition flex items-center justify-center gap-4"
              >
                {submittingComplaint ? (
                  <>
                    <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={32} />
                    Submit Complaint
                  </>
                )}
              </button>
            </div>
          </div>

          {/* List of Complaints */}
          {complaints.length > 0 && (
            <div className="relative">
              <div className="overflow-hidden rounded-3xl" ref={complaintEmblaRef}>
                <div className="flex">
                  {complaints.map((c) => (
                    <div key={c.id} className="flex-none w-full px-6">
                      <div className="bg-white rounded-3xl shadow-2xl p-12 border-l-8 border-yellow-500">
                        <h3 className="text-3xl font-bold text-gray-900">
                          {COMPLAINT_SUBJECTS.find(s => s.value === c.subject)?.label || c.subject}
                        </h3>
                        <p className="text-xl text-gray-700 mt-6 leading-relaxed">{c.complaint_details}</p>
                        <div className="mt-8 flex items-center justify-between text-lg">
                          <span className={`px-6 py-3 rounded-full font-bold ${c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {c.status?.toUpperCase() || 'PENDING'}
                          </span>
                          <span className="text-gray-500">{formatDate(c.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {complaints.length > 1 && (
                <>
                  <button
                    onClick={() => complaintEmblaApi?.scrollPrev()}
                    disabled={complaintIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-4 rounded-full shadow-2xl disabled:opacity-30"
                  >
                    <ChevronLeft size={48} />
                  </button>
                  <button
                    onClick={() => complaintEmblaApi?.scrollNext()}
                    disabled={complaintIndex === complaints.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-4 rounded-full shadow-2xl disabled:opacity-30"
                  >
                    <ChevronRight size={48} />
                  </button>
                </>
              )}
            </div>
          )}

          {complaints.length === 0 && (
            <div className="text-center py-20 bg-gray-50 rounded-3xl">
              <MessageSquare size={80} className="mx-auto text-gray-300 mb-6" />
              <p className="text-2xl text-gray-600">No complaints submitted yet</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}